import type { LucideIcon } from "lucide-react-native";
import type { RequestBody } from "@src/packages/openapi/helpers";
import type { paths } from "@src/packages/openapi/schema";
import {
  Activity,
  Bug,
  Heart,
  Wind,
  Apple,
  FlaskConical,
  Droplets,
  Bone,
  Syringe,
  ShieldAlert,
  Dna,
  UtensilsCrossed,
  Brain,
  MoreHorizontal,
  Pill,
  Thermometer,
  Scissors,
  TrendingUp,
  Zap,
  TrendingDown,
  HeartPulse,
  Eye,
  CloudRain,
  Stethoscope,
  AlertCircle,
  CircleDot,
  Scale,
  Flame,
} from "lucide-react-native";

// ── Disease category types ──────────────────────────────

export type DiseaseCategoryType =
  | "endocrine"
  | "infectious"
  | "cardiovascular"
  | "respiratory"
  | "gastrointestinal"
  | "hepatic_biliary"
  | "renal_urinary"
  | "musculoskeletal"
  | "hematological"
  | "autoimmune"
  | "genetic"
  | "eating_disorders"
  | "neurological_mental_health";

const CATEGORY_LABEL_KEY = (id: DiseaseCategoryType): string =>
  `med_history.medical.disease_history.disease_categories.categories.${id}`;

const CATEGORY_QUESTION_KEY = (id: DiseaseCategoryType): string =>
  `med_history.medical.disease_history.disease_categories.category_questions.${id}`;

export const DISEASE_CATEGORY_OPTIONS: {
  id: DiseaseCategoryType;
  labelKey: string;
  questionTitleKey: string;
  icon: LucideIcon;
}[] = [
  {
    id: "endocrine",
    labelKey: CATEGORY_LABEL_KEY("endocrine"),
    questionTitleKey: CATEGORY_QUESTION_KEY("endocrine"),
    icon: Activity,
  },
  {
    id: "infectious",
    labelKey: CATEGORY_LABEL_KEY("infectious"),
    questionTitleKey: CATEGORY_QUESTION_KEY("infectious"),
    icon: Bug,
  },
  {
    id: "cardiovascular",
    labelKey: CATEGORY_LABEL_KEY("cardiovascular"),
    questionTitleKey: CATEGORY_QUESTION_KEY("cardiovascular"),
    icon: Heart,
  },
  {
    id: "respiratory",
    labelKey: CATEGORY_LABEL_KEY("respiratory"),
    questionTitleKey: CATEGORY_QUESTION_KEY("respiratory"),
    icon: Wind,
  },
  {
    id: "gastrointestinal",
    labelKey: CATEGORY_LABEL_KEY("gastrointestinal"),
    questionTitleKey: CATEGORY_QUESTION_KEY("gastrointestinal"),
    icon: Apple,
  },
  {
    id: "hepatic_biliary",
    labelKey: CATEGORY_LABEL_KEY("hepatic_biliary"),
    questionTitleKey: CATEGORY_QUESTION_KEY("hepatic_biliary"),
    icon: FlaskConical,
  },
  {
    id: "renal_urinary",
    labelKey: CATEGORY_LABEL_KEY("renal_urinary"),
    questionTitleKey: CATEGORY_QUESTION_KEY("renal_urinary"),
    icon: Droplets,
  },
  {
    id: "musculoskeletal",
    labelKey: CATEGORY_LABEL_KEY("musculoskeletal"),
    questionTitleKey: CATEGORY_QUESTION_KEY("musculoskeletal"),
    icon: Bone,
  },
  {
    id: "hematological",
    labelKey: CATEGORY_LABEL_KEY("hematological"),
    questionTitleKey: CATEGORY_QUESTION_KEY("hematological"),
    icon: Syringe,
  },
  {
    id: "autoimmune",
    labelKey: CATEGORY_LABEL_KEY("autoimmune"),
    questionTitleKey: CATEGORY_QUESTION_KEY("autoimmune"),
    icon: ShieldAlert,
  },
  {
    id: "genetic",
    labelKey: CATEGORY_LABEL_KEY("genetic"),
    questionTitleKey: CATEGORY_QUESTION_KEY("genetic"),
    icon: Dna,
  },
  {
    id: "eating_disorders",
    labelKey: CATEGORY_LABEL_KEY("eating_disorders"),
    questionTitleKey: CATEGORY_QUESTION_KEY("eating_disorders"),
    icon: UtensilsCrossed,
  },
  {
    id: "neurological_mental_health",
    labelKey: CATEGORY_LABEL_KEY("neurological_mental_health"),
    questionTitleKey: CATEGORY_QUESTION_KEY("neurological_mental_health"),
    icon: Brain,
  },
];

// ── Disease types (from API schema) ─────────────────────

export type DiseaseType =
  | "diabetes"
  | "pcos"
  | "thyroid_disease"
  | "excessive_body_or_facial_hair"
  | "hair_loss"
  | "rapid_weight_gain_or_loss"
  | "temperature_intolerance"
  | "excessive_hunger_or_thirst"
  | "hypothalamic_pituitary"
  | "hyperthyroidism_failure"
  | "primary_ovarian_insufficiency"
  | "diminished_ovarian_reserve"
  | "rubella"
  | "chicken_pox"
  | "measles"
  | "mumps"
  | "rheumatic_fever"
  | "covid_19"
  | "elevated_blood_pressure"
  | "blood_clots"
  | "heart_murmur"
  | "mitral_valve_prolapse"
  | "stroke"
  | "seizures"
  | "lung_disease"
  | "asthma"
  | "irritable_bowel_syndrome"
  | "frequent_nausea_vomiting"
  | "blood_in_stool"
  | "frequent_diarrhea"
  | "liver_disease"
  | "gallbladder_disease"
  | "kidney_issues"
  | "frequent_urination"
  | "bladder_infection"
  | "blood_in_urine"
  | "frequent_urinary_tract_infections"
  | "arthritis"
  | "gout"
  | "fibrodysplasia_ossificans_progressiva"
  | "malignant_hyperthermia"
  | "easy_bruising"
  | "sickle_cell_anemia"
  | "lupus"
  | "rheumatoid_arthritis"
  | "multiple_sclerosis"
  | "vitiligo"
  | "crohns_disease"
  | "bulimia"
  | "anorexia"
  | "orthorexia"
  | "binge_eating_disorder"
  | "rumination_disorder"
  | "anxiety"
  | "depression"
  | "bipolar_disorder"
  | "schizophrenia"
  | "endometriosis"
  | "klinefelter_syndrome"
  | "noonan_syndrome"
  | "cftr"
  | "y_chromosome_microdeletions"
  | "other";

// ── Per-category disease options ────────────────────────

export type DiseaseSex = "AFAB" | "AMAB" | "both";

interface DiseaseOption {
  id: DiseaseType;
  labelKey: string;
  icon: LucideIcon;
  sex: DiseaseSex;
  /** ICD-10 base code (WHO) — shared across country variants (CM, AM, GM, FR) */
  icd10?: string;
  /** SNOMED CT concept ID — international clinical terminology */
  snomedCt?: string;
}

type PatchPayload = RequestBody<
  paths["/{tenantId}/patients/{patientId}"]["patch"]
>;
type MedicalHistoryPayload = NonNullable<PatchPayload["medicalHistory"]>;
type DiseasePayload = NonNullable<MedicalHistoryPayload["diseases"]>[number];
type DiseaseSourceCode = NonNullable<DiseasePayload["sourceCodes"]>[number];

const DISEASE_LABEL_KEY = (
  category: DiseaseCategoryType,
  id: DiseaseType,
): string => `med_history.medical.disease_history.diseases.${category}.${id}`;

export const DISEASES_BY_CATEGORY: Record<
  DiseaseCategoryType,
  DiseaseOption[]
> = {
  endocrine: [
    {
      id: "diabetes",
      labelKey: DISEASE_LABEL_KEY("endocrine", "diabetes"),
      icon: Pill,
      sex: "both",
      icd10: "E11",
      snomedCt: "73211009",
    },
    {
      id: "pcos",
      labelKey: DISEASE_LABEL_KEY("endocrine", "pcos"),
      icon: CircleDot,
      sex: "both",
      icd10: "E28.2",
      snomedCt: "69878008",
    },
    {
      id: "thyroid_disease",
      labelKey: DISEASE_LABEL_KEY("endocrine", "thyroid_disease"),
      icon: Activity,
      sex: "both",
      icd10: "E07.9",
      snomedCt: "14304000",
    },
    {
      id: "excessive_body_or_facial_hair",
      labelKey: DISEASE_LABEL_KEY("endocrine", "excessive_body_or_facial_hair"),
      icon: Scissors,
      sex: "both",
      icd10: "L68.0",
      snomedCt: "399068003",
    },
    {
      id: "hair_loss",
      labelKey: DISEASE_LABEL_KEY("endocrine", "hair_loss"),
      icon: TrendingDown,
      sex: "both",
      icd10: "L65.9",
      snomedCt: "278040002",
    },
    {
      id: "rapid_weight_gain_or_loss",
      labelKey: DISEASE_LABEL_KEY("endocrine", "rapid_weight_gain_or_loss"),
      icon: TrendingUp,
      sex: "both",
      icd10: "R63.4",
      snomedCt: "267024001",
    },
    {
      id: "temperature_intolerance",
      labelKey: DISEASE_LABEL_KEY("endocrine", "temperature_intolerance"),
      icon: Thermometer,
      sex: "both",
      icd10: "R68.89",
      snomedCt: "248430008",
    },
    {
      id: "excessive_hunger_or_thirst",
      labelKey: DISEASE_LABEL_KEY("endocrine", "excessive_hunger_or_thirst"),
      icon: Droplets,
      sex: "both",
      icd10: "R63.2",
      snomedCt: "17173007",
    },
    {
      id: "hypothalamic_pituitary",
      labelKey: DISEASE_LABEL_KEY("endocrine", "hypothalamic_pituitary"),
      icon: Brain,
      sex: "both",
      icd10: "E23.7",
      snomedCt: "74728003",
    },
    {
      id: "hyperthyroidism_failure",
      labelKey: DISEASE_LABEL_KEY("endocrine", "hyperthyroidism_failure"),
      icon: Zap,
      sex: "both",
      icd10: "E05.9",
      snomedCt: "34486009",
    },
    {
      id: "primary_ovarian_insufficiency",
      labelKey: DISEASE_LABEL_KEY("endocrine", "primary_ovarian_insufficiency"),
      icon: AlertCircle,
      sex: "AFAB",
      icd10: "E28.3",
      snomedCt: "237788002",
    },
    {
      id: "diminished_ovarian_reserve",
      labelKey: DISEASE_LABEL_KEY("endocrine", "diminished_ovarian_reserve"),
      icon: TrendingDown,
      sex: "AFAB",
      icd10: "E28.8",
      snomedCt: "698632006",
    },
    {
      id: "other",
      labelKey: DISEASE_LABEL_KEY("endocrine", "other"),
      icon: MoreHorizontal,
      sex: "both",
    },
  ],
  infectious: [
    {
      id: "rubella",
      labelKey: DISEASE_LABEL_KEY("infectious", "rubella"),
      icon: Bug,
      sex: "both",
      icd10: "B06",
      snomedCt: "36653000",
    },
    {
      id: "chicken_pox",
      labelKey: DISEASE_LABEL_KEY("infectious", "chicken_pox"),
      icon: Bug,
      sex: "both",
      icd10: "B01",
      snomedCt: "38907003",
    },
    {
      id: "measles",
      labelKey: DISEASE_LABEL_KEY("infectious", "measles"),
      icon: Bug,
      sex: "both",
      icd10: "B05",
      snomedCt: "14189004",
    },
    {
      id: "mumps",
      labelKey: DISEASE_LABEL_KEY("infectious", "mumps"),
      icon: Bug,
      sex: "both",
      icd10: "B26",
      snomedCt: "36989005",
    },
    {
      id: "rheumatic_fever",
      labelKey: DISEASE_LABEL_KEY("infectious", "rheumatic_fever"),
      icon: Thermometer,
      sex: "both",
      icd10: "I00",
      snomedCt: "58718002",
    },
    {
      id: "covid_19",
      labelKey: DISEASE_LABEL_KEY("infectious", "covid_19"),
      icon: Bug,
      sex: "both",
      icd10: "U07.1",
      snomedCt: "840539006",
    },
    {
      id: "other",
      labelKey: DISEASE_LABEL_KEY("infectious", "other"),
      icon: MoreHorizontal,
      sex: "both",
    },
  ],
  cardiovascular: [
    {
      id: "elevated_blood_pressure",
      labelKey: DISEASE_LABEL_KEY("cardiovascular", "elevated_blood_pressure"),
      icon: HeartPulse,
      sex: "both",
      icd10: "I10",
      snomedCt: "38341003",
    },
    {
      id: "blood_clots",
      labelKey: DISEASE_LABEL_KEY("cardiovascular", "blood_clots"),
      icon: Droplets,
      sex: "both",
      icd10: "I82.9",
      snomedCt: "111293003",
    },
    {
      id: "heart_murmur",
      labelKey: DISEASE_LABEL_KEY("cardiovascular", "heart_murmur"),
      icon: Heart,
      sex: "both",
      icd10: "R01.1",
      snomedCt: "88610006",
    },
    {
      id: "mitral_valve_prolapse",
      labelKey: DISEASE_LABEL_KEY("cardiovascular", "mitral_valve_prolapse"),
      icon: Heart,
      sex: "both",
      icd10: "I34.1",
      snomedCt: "409712001",
    },
    {
      id: "stroke",
      labelKey: DISEASE_LABEL_KEY("cardiovascular", "stroke"),
      icon: Zap,
      sex: "both",
      icd10: "I63.9",
      snomedCt: "230690007",
    },
    {
      id: "other",
      labelKey: DISEASE_LABEL_KEY("cardiovascular", "other"),
      icon: MoreHorizontal,
      sex: "both",
    },
  ],
  respiratory: [
    {
      id: "lung_disease",
      labelKey: DISEASE_LABEL_KEY("respiratory", "lung_disease"),
      icon: Wind,
      sex: "both",
      icd10: "J98.4",
      snomedCt: "19829001",
    },
    {
      id: "asthma",
      labelKey: DISEASE_LABEL_KEY("respiratory", "asthma"),
      icon: CloudRain,
      sex: "both",
      icd10: "J45",
      snomedCt: "195967001",
    },
    {
      id: "seizures",
      labelKey: DISEASE_LABEL_KEY("respiratory", "seizures"),
      icon: Zap,
      sex: "both",
      icd10: "R56.9",
      snomedCt: "91175000",
    },
    {
      id: "other",
      labelKey: DISEASE_LABEL_KEY("respiratory", "other"),
      icon: MoreHorizontal,
      sex: "both",
    },
  ],
  gastrointestinal: [
    {
      id: "irritable_bowel_syndrome",
      labelKey: DISEASE_LABEL_KEY(
        "gastrointestinal",
        "irritable_bowel_syndrome",
      ),
      icon: Apple,
      sex: "both",
      icd10: "K58",
      snomedCt: "10743008",
    },
    {
      id: "frequent_nausea_vomiting",
      labelKey: DISEASE_LABEL_KEY(
        "gastrointestinal",
        "frequent_nausea_vomiting",
      ),
      icon: AlertCircle,
      sex: "both",
      icd10: "R11",
      snomedCt: "422587007",
    },
    {
      id: "blood_in_stool",
      labelKey: DISEASE_LABEL_KEY("gastrointestinal", "blood_in_stool"),
      icon: Droplets,
      sex: "both",
      icd10: "K92.1",
      snomedCt: "405729008",
    },
    {
      id: "frequent_diarrhea",
      labelKey: DISEASE_LABEL_KEY("gastrointestinal", "frequent_diarrhea"),
      icon: AlertCircle,
      sex: "both",
      icd10: "K59.1",
      snomedCt: "62315008",
    },
    {
      id: "other",
      labelKey: DISEASE_LABEL_KEY("gastrointestinal", "other"),
      icon: MoreHorizontal,
      sex: "both",
    },
  ],
  hepatic_biliary: [
    {
      id: "liver_disease",
      labelKey: DISEASE_LABEL_KEY("hepatic_biliary", "liver_disease"),
      icon: FlaskConical,
      sex: "both",
      icd10: "K76.9",
      snomedCt: "235856003",
    },
    {
      id: "gallbladder_disease",
      labelKey: DISEASE_LABEL_KEY("hepatic_biliary", "gallbladder_disease"),
      icon: Flame,
      sex: "both",
      icd10: "K82.9",
      snomedCt: "39621005",
    },
    {
      id: "other",
      labelKey: DISEASE_LABEL_KEY("hepatic_biliary", "other"),
      icon: MoreHorizontal,
      sex: "both",
    },
  ],
  renal_urinary: [
    {
      id: "kidney_issues",
      labelKey: DISEASE_LABEL_KEY("renal_urinary", "kidney_issues"),
      icon: Droplets,
      sex: "both",
      icd10: "N28.9",
      snomedCt: "90708001",
    },
    {
      id: "frequent_urination",
      labelKey: DISEASE_LABEL_KEY("renal_urinary", "frequent_urination"),
      icon: Droplets,
      sex: "both",
      icd10: "R35.0",
      snomedCt: "162116003",
    },
    {
      id: "bladder_infection",
      labelKey: DISEASE_LABEL_KEY("renal_urinary", "bladder_infection"),
      icon: Bug,
      sex: "both",
      icd10: "N30.0",
      snomedCt: "38822007",
    },
    {
      id: "blood_in_urine",
      labelKey: DISEASE_LABEL_KEY("renal_urinary", "blood_in_urine"),
      icon: Droplets,
      sex: "both",
      icd10: "R31",
      snomedCt: "34436003",
    },
    {
      id: "frequent_urinary_tract_infections",
      labelKey: DISEASE_LABEL_KEY(
        "renal_urinary",
        "frequent_urinary_tract_infections",
      ),
      icon: Bug,
      sex: "both",
      icd10: "N39.0",
      snomedCt: "68566005",
    },
    {
      id: "other",
      labelKey: DISEASE_LABEL_KEY("renal_urinary", "other"),
      icon: MoreHorizontal,
      sex: "both",
    },
  ],
  musculoskeletal: [
    {
      id: "arthritis",
      labelKey: DISEASE_LABEL_KEY("musculoskeletal", "arthritis"),
      icon: Bone,
      sex: "both",
      icd10: "M13.9",
      snomedCt: "3723001",
    },
    {
      id: "gout",
      labelKey: DISEASE_LABEL_KEY("musculoskeletal", "gout"),
      icon: Flame,
      sex: "both",
      icd10: "M10",
      snomedCt: "90560007",
    },
    {
      id: "fibrodysplasia_ossificans_progressiva",
      labelKey: DISEASE_LABEL_KEY(
        "musculoskeletal",
        "fibrodysplasia_ossificans_progressiva",
      ),
      icon: Bone,
      sex: "both",
      icd10: "M61.1",
      snomedCt: "55602000",
    },
    {
      id: "malignant_hyperthermia",
      labelKey: DISEASE_LABEL_KEY("musculoskeletal", "malignant_hyperthermia"),
      icon: Thermometer,
      sex: "both",
      icd10: "T88.3",
      snomedCt: "405501007",
    },
    {
      id: "other",
      labelKey: DISEASE_LABEL_KEY("musculoskeletal", "other"),
      icon: MoreHorizontal,
      sex: "both",
    },
  ],
  hematological: [
    {
      id: "easy_bruising",
      labelKey: DISEASE_LABEL_KEY("hematological", "easy_bruising"),
      icon: AlertCircle,
      sex: "both",
      icd10: "D69.9",
      snomedCt: "302227002",
    },
    {
      id: "sickle_cell_anemia",
      labelKey: DISEASE_LABEL_KEY("hematological", "sickle_cell_anemia"),
      icon: Syringe,
      sex: "both",
      icd10: "D57",
      snomedCt: "417357006",
    },
    {
      id: "other",
      labelKey: DISEASE_LABEL_KEY("hematological", "other"),
      icon: MoreHorizontal,
      sex: "both",
    },
  ],
  autoimmune: [
    {
      id: "lupus",
      labelKey: DISEASE_LABEL_KEY("autoimmune", "lupus"),
      icon: ShieldAlert,
      sex: "both",
      icd10: "M32.9",
      snomedCt: "55464009",
    },
    {
      id: "rheumatoid_arthritis",
      labelKey: DISEASE_LABEL_KEY("autoimmune", "rheumatoid_arthritis"),
      icon: Bone,
      sex: "both",
      icd10: "M06.9",
      snomedCt: "69896004",
    },
    {
      id: "multiple_sclerosis",
      labelKey: DISEASE_LABEL_KEY("autoimmune", "multiple_sclerosis"),
      icon: Brain,
      sex: "both",
      icd10: "G35",
      snomedCt: "24700007",
    },
    {
      id: "vitiligo",
      labelKey: DISEASE_LABEL_KEY("autoimmune", "vitiligo"),
      icon: Eye,
      sex: "both",
      icd10: "L80",
      snomedCt: "56727007",
    },
    {
      id: "crohns_disease",
      labelKey: DISEASE_LABEL_KEY("autoimmune", "crohns_disease"),
      icon: Apple,
      sex: "both",
      icd10: "K50",
      snomedCt: "34000006",
    },
    {
      id: "other",
      labelKey: DISEASE_LABEL_KEY("autoimmune", "other"),
      icon: MoreHorizontal,
      sex: "both",
    },
  ],
  genetic: [
    {
      id: "klinefelter_syndrome",
      labelKey: DISEASE_LABEL_KEY("genetic", "klinefelter_syndrome"),
      icon: Dna,
      sex: "AMAB",
      icd10: "Q98.0",
      snomedCt: "405769009",
    },
    {
      id: "noonan_syndrome",
      labelKey: DISEASE_LABEL_KEY("genetic", "noonan_syndrome"),
      icon: Dna,
      sex: "both",
      icd10: "Q87.1",
      snomedCt: "205824006",
    },
    {
      id: "cftr",
      labelKey: DISEASE_LABEL_KEY("genetic", "cftr"),
      icon: Dna,
      sex: "both",
      icd10: "E84",
      snomedCt: "190905008",
    },
    {
      id: "y_chromosome_microdeletions",
      labelKey: DISEASE_LABEL_KEY("genetic", "y_chromosome_microdeletions"),
      icon: Dna,
      sex: "AMAB",
      icd10: "Q98.8",
      snomedCt: "724150008",
    },
    {
      id: "other",
      labelKey: DISEASE_LABEL_KEY("genetic", "other"),
      icon: MoreHorizontal,
      sex: "both",
    },
  ],
  eating_disorders: [
    {
      id: "bulimia",
      labelKey: DISEASE_LABEL_KEY("eating_disorders", "bulimia"),
      icon: UtensilsCrossed,
      sex: "both",
      icd10: "F50.2",
      snomedCt: "78004001",
    },
    {
      id: "anorexia",
      labelKey: DISEASE_LABEL_KEY("eating_disorders", "anorexia"),
      icon: UtensilsCrossed,
      sex: "both",
      icd10: "F50.0",
      snomedCt: "56882008",
    },
    {
      id: "orthorexia",
      labelKey: DISEASE_LABEL_KEY("eating_disorders", "orthorexia"),
      icon: Scale,
      sex: "both",
      icd10: "F50.8",
      snomedCt: "72366004",
    },
    {
      id: "binge_eating_disorder",
      labelKey: DISEASE_LABEL_KEY("eating_disorders", "binge_eating_disorder"),
      icon: Apple,
      sex: "both",
      icd10: "F50.81",
      snomedCt: "439960005",
    },
    {
      id: "rumination_disorder",
      labelKey: DISEASE_LABEL_KEY("eating_disorders", "rumination_disorder"),
      icon: AlertCircle,
      sex: "both",
      icd10: "F98.21",
      snomedCt: "69482004",
    },
    {
      id: "other",
      labelKey: DISEASE_LABEL_KEY("eating_disorders", "other"),
      icon: MoreHorizontal,
      sex: "both",
    },
  ],
  neurological_mental_health: [
    {
      id: "anxiety",
      labelKey: DISEASE_LABEL_KEY("neurological_mental_health", "anxiety"),
      icon: AlertCircle,
      sex: "both",
      icd10: "F41.1",
      snomedCt: "48694002",
    },
    {
      id: "depression",
      labelKey: DISEASE_LABEL_KEY("neurological_mental_health", "depression"),
      icon: CloudRain,
      sex: "both",
      icd10: "F32.9",
      snomedCt: "35489007",
    },
    {
      id: "bipolar_disorder",
      labelKey: DISEASE_LABEL_KEY(
        "neurological_mental_health",
        "bipolar_disorder",
      ),
      icon: Activity,
      sex: "both",
      icd10: "F31",
      snomedCt: "13746004",
    },
    {
      id: "schizophrenia",
      labelKey: DISEASE_LABEL_KEY(
        "neurological_mental_health",
        "schizophrenia",
      ),
      icon: Brain,
      sex: "both",
      icd10: "F20",
      snomedCt: "58214004",
    },
    {
      id: "endometriosis",
      labelKey: DISEASE_LABEL_KEY(
        "neurological_mental_health",
        "endometriosis",
      ),
      icon: Stethoscope,
      sex: "AFAB",
      icd10: "N80",
      snomedCt: "129103003",
    },
    {
      id: "other",
      labelKey: DISEASE_LABEL_KEY("neurological_mental_health", "other"),
      icon: MoreHorizontal,
      sex: "both",
    },
  ],
};

export function getDiseaseLabelKey(
  category: DiseaseCategoryType,
  disease: string,
): string {
  const options = DISEASES_BY_CATEGORY[category];
  return (
    options?.find((o) => o.id === disease)?.labelKey ??
    DISEASE_LABEL_KEY(category, "other")
  );
}

/**
 * Return the terminology assertions authored with a curated intake option.
 * The raw disease/other fields remain the source label; these codes are
 * additional assertions and are never used as record identity.
 */
export function getDiseaseSourceCodes(
  category: DiseaseCategoryType,
  disease: string,
  display: string,
): DiseaseSourceCode[] {
  const option = DISEASES_BY_CATEGORY[category]?.find(
    ({ id }) => id === disease,
  );
  if (!option) return [];

  const codes: DiseaseSourceCode[] = [];
  if (option.icd10) {
    codes.push({ system: "ICD10", code: option.icd10, display });
  }
  if (option.snomedCt) {
    codes.push({
      system: "SNOMED_CT",
      code: option.snomedCt,
      display,
    });
  }
  return codes;
}

// ── Category flag name mapping ──────────────────────────

export const CATEGORY_FLAG_MAP: Record<DiseaseCategoryType, string> = {
  endocrine: "hasEndocrineDiseases",
  infectious: "hasInfectiousDiseases",
  cardiovascular: "hasCardiovascularDiseases",
  respiratory: "hasRespiratoryDiseases",
  gastrointestinal: "hasGastrointestinalDiseases",
  hepatic_biliary: "hasHepaticBiliaryDiseases",
  renal_urinary: "hasRenalUrinaryDiseases",
  musculoskeletal: "hasMusculoskeletalDiseases",
  hematological: "hasHaematologicalDiseases",
  autoimmune: "hasAutoimmuneDiseases",
  genetic: "hasGeneticDiseases",
  eating_disorders: "hasEatingDisorders",
  neurological_mental_health: "hasNeurologicalMentalHealthDiseases",
};

/**
 * i18n key map for category labels. Use with `t(CATEGORY_LABEL_KEY_MAP[category])`.
 * Replaces the previous English-only `CATEGORY_LABEL_MAP`.
 */
export const CATEGORY_LABEL_KEY_MAP: Record<DiseaseCategoryType, string> = {
  endocrine: CATEGORY_LABEL_KEY("endocrine"),
  infectious: CATEGORY_LABEL_KEY("infectious"),
  cardiovascular: CATEGORY_LABEL_KEY("cardiovascular"),
  respiratory: CATEGORY_LABEL_KEY("respiratory"),
  gastrointestinal: CATEGORY_LABEL_KEY("gastrointestinal"),
  hepatic_biliary: CATEGORY_LABEL_KEY("hepatic_biliary"),
  renal_urinary: CATEGORY_LABEL_KEY("renal_urinary"),
  musculoskeletal: CATEGORY_LABEL_KEY("musculoskeletal"),
  hematological: CATEGORY_LABEL_KEY("hematological"),
  autoimmune: CATEGORY_LABEL_KEY("autoimmune"),
  genetic: CATEGORY_LABEL_KEY("genetic"),
  eating_disorders: CATEGORY_LABEL_KEY("eating_disorders"),
  neurological_mental_health: CATEGORY_LABEL_KEY("neurological_mental_health"),
};
